import { useCallback, useEffect, useRef, useState } from 'react';

interface SettingsLeaveGuardOptions {
  dirty: boolean;
  busy: boolean;
  onDiscard: () => void;
}

/** All settings departures use the same draft and in-flight save protection. */
export function useSettingsLeaveGuard({ dirty, busy, onDiscard }: SettingsLeaveGuardOptions) {
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const pendingAction = useRef<(() => void) | null>(null);

  const requestLeave = useCallback(
    (action: () => void) => {
      if (busy) return;
      if (!dirty) {
        action();
        return;
      }
      pendingAction.current = action;
      setConfirmationOpen(true);
    },
    [busy, dirty]
  );

  const keepEditing = useCallback(() => {
    pendingAction.current = null;
    setConfirmationOpen(false);
  }, []);

  const discardAndLeave = useCallback(() => {
    if (busy || !pendingAction.current) return;
    const action = pendingAction.current;
    pendingAction.current = null;
    setConfirmationOpen(false);
    onDiscard();
    action();
  }, [busy, onDiscard]);

  useEffect(() => {
    if (!dirty && !busy) return;
    const protectDeparture = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', protectDeparture);
    return () => window.removeEventListener('beforeunload', protectDeparture);
  }, [dirty, busy]);

  return { requestLeave, confirmationOpen, keepEditing, discardAndLeave };
}
